// src/catalog/utils/catalogDisplay.js

import languageOptions from "../../constants/languageOptions";

const LANGUAGE_LABELS = Object.fromEntries(
  languageOptions.map((language) => [
    language.value.toLowerCase(),
    language.label
  ])
);

const LANGUAGE_CODE_ALIASES = {
  jp: "ja"
};

const LANGUAGE_LABEL_OVERRIDES = {
  fr: "French (Europe)",
  "fr-ca": "French (Québec)",
  my: "Malay"
};

export function humanizeValue(value) {
  if (!value) return null;

  const text = String(value).trim();

  if (!text) return null;

  const parts = text.split(":");

  if (parts.length > 1) {
    const namespace = humanizeSimple(parts[0]);
    const label = humanizeSimple(parts.slice(1).join(":"));

    return `${namespace}: ${label}`;
  }

  return humanizeSimple(text);
}

function humanizeSimple(value) {
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

export function formatLabel(value) {
  return humanizeValue(value);
}

export function formatResourceFormat(value) {
  if (!value) return null;

  const normalized = String(value).trim().toLowerCase();

  if (normalized === "pdf") {
    return "PDF";
  }

  return humanizeValue(value);
}

export function formatTagLabel(tag) {
  if (!tag) return null;

  const text = String(tag).trim();

  if (!text) return null;

  const [namespace, ...rest] = text.split(":");

  if (rest.length === 0) {
    return humanizeValue(text);
  }

  const value = rest.join(":");

  if (namespace === "language") {
    return `Language: ${formatLanguage(value)}`;
  }

  if (namespace === "voice_language") {
    return `Voice language: ${formatLanguage(value)}`;
  }

  return `${humanizeValue(namespace)}: ${humanizeValue(value)}`;
}

export function formatLanguage(code) {
  if (!code) return null;

  const normalized = String(code).trim().toLowerCase();
  if (LANGUAGE_LABEL_OVERRIDES[normalized]) {
    return LANGUAGE_LABEL_OVERRIDES[normalized];
  }
  const canonicalCode =
    LANGUAGE_CODE_ALIASES[normalized] || normalized;

  if (LANGUAGE_LABELS[canonicalCode]) {
    return LANGUAGE_LABELS[canonicalCode];
  }

  const baseLanguage = canonicalCode.split("-")[0];

  if (LANGUAGE_LABELS[baseLanguage]) {
    return LANGUAGE_LABELS[baseLanguage];
  }

  return humanizeValue(code);
}

export function formatLanguageList(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return "Not specified";
  }

  return values
    .map(formatLanguage)
    .filter(Boolean)
    .join(", ");
}

function toTitleCase(text) {
  return String(text)
    .split(" ")
    .filter(Boolean)
    .map((word) => (
      word.length <= 2 && word === word.toLowerCase()
        ? word.charAt(0).toUpperCase() + word.slice(1)
        : word.charAt(0).toUpperCase() + word.slice(1)
    ))
    .join(" ");
}

function normalizeFacetValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

const ACTIVITY_FACET_PRIORITY = [
  "learner_assessment",
  "research",
  "data_generation",
  "calibration",
  "education"
];

function getEntryPurposeValues(entry) {
  const purposeDomains = Array.isArray(entry?.purposeDomains)
    ? entry.purposeDomains
    : [];
  const purposeTags = Array.isArray(entry?.tags)
    ? entry.tags
      .filter((tag) => String(tag).startsWith("purpose:"))
      .map((tag) => tag.split(":").slice(1).join(":"))
    : [];

  return Array.from(
    new Set(
      [...purposeDomains, ...purposeTags]
        .map(normalizeFacetValue)
        .filter(Boolean)
    )
  );
}

function formatCardFacetValue(value) {
  const label = humanizeValue(value);
  return label ? toTitleCase(label) : null;
}

function getActivityCardFacets(entry, maxFacets) {
  const purposes = getEntryPurposeValues(entry);

  const prioritized = ACTIVITY_FACET_PRIORITY
    .filter((value) => purposes.includes(value))
    .map(formatCardFacetValue)
    .filter(Boolean);

  const remaining = purposes
    .filter((value) => !ACTIVITY_FACET_PRIORITY.includes(value))
    .map(formatCardFacetValue)
    .filter(Boolean);

  return [...prioritized, ...remaining].slice(0, maxFacets);
}

const GENERIC_CARD_TAG_EXCLUSIONS = new Set([
  "primary_type",
  "domain"
]);

function getGenericCardFacets(entry, maxFacets) {
  const facets = Array.isArray(entry?.tags)
    ? entry.tags
      .map((tag) => String(tag || "").trim())
      .filter(Boolean)
      .map((tag) => {
        const [namespace, ...rest] = tag.split(":");
        const value = rest.join(":");

        if (!value || GENERIC_CARD_TAG_EXCLUSIONS.has(namespace)) {
          return null;
        }

        if (namespace === "purpose") {
          return formatCardFacetValue(value);
        }

        return formatTagLabel(tag);
      })
      .filter(Boolean)
    : [];

  return Array.from(new Set(facets)).slice(0, maxFacets);
}

export function getCatalogCardFacetLabels(
  entry,
  { maxFacets = 3 } = {}
) {
  if (!entry || maxFacets <= 0) {
    return [];
  }

  if (entry.primaryType === "activity") {
    return getActivityCardFacets(entry, maxFacets);
  }

  return getGenericCardFacets(entry, maxFacets);
}

export function getEntryTitle(entry, t) {
  if (!entry) return "Untitled";

  const fallback =
    entry.title ||
    entry.display?.title ||
    humanizeValue(entry.slug) ||
    "Untitled";

  if (entry.titleKey && typeof t === "function") {
    return t(entry.titleKey, {
      defaultValue: fallback
    });
  }

  return fallback;
}

export function getEntryShortDescription(entry, t) {
  if (!entry) return "";

  const fallback =
    entry.shortDescription ||
    entry.description ||
    entry.display?.shortDescription ||
    entry.display?.description ||
    "";

  if (entry.shortDescriptionKey && typeof t === "function") {
    return t(entry.shortDescriptionKey, {
      defaultValue: fallback
    });
  }

  return fallback;
}

export function getEntryDescription(entry, t) {
  if (!entry) return "";

  const fallback =
    entry.description ||
    entry.shortDescription ||
    entry.display?.description ||
    entry.display?.shortDescription ||
    "";

  if (entry.descriptionKey && typeof t === "function") {
    return t(entry.descriptionKey, {
      defaultValue: fallback
    });
  }

  if (entry.shortDescriptionKey && typeof t === "function") {
    return t(entry.shortDescriptionKey, {
      defaultValue: fallback
    });
  }

  return fallback;
}

export function getContactEmail(entry) {
  return (
    entry?.contactEmail ||
    entry?.metadata?.contact?.email ||
    entry?.metadata?.collaboration?.contactEmail ||
    entry?.commercial?.contactEmail ||
    null
  );
}

export function getExternalLinks(entry, surface = "app") {
  const links = Array.isArray(entry?.links)
    ? entry.links
    : [];

  return links.filter((link) => {
    if (!link?.url) return false;

    if (link.visibility?.[surface] === false) {
      return false;
    }

    return true;
  });
}
